import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Truck,
  Search,
  X,
  Phone,
  Calendar,
  CheckCircle2,
  ChevronRight,
  RotateCcw,
  RefreshCw,
  AlertTriangle,
  Users,
  Clock,
  User,
  ArrowRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { isAdmin, canAccessDepartment, isDeliveryUser } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";
import DeliveryPersonDetail from "./DeliveryPersonDetail";

function OrderDescriptionSnippet({ description, maxLength = 80, testId }) {
  const [expanded, setExpanded] = useState(false);
  const trimmed = (description || "").trim();

  if (!trimmed) {
    return (
      <div
        style={{
          fontSize: 11,
          color: "#94a3b8",
          fontStyle: "italic",
          marginTop: 2,
          lineHeight: 1.3,
        }}
        data-testid={testId}
      >
        No description provided
      </div>
    );
  }

  const isLong = trimmed.length > maxLength;
  const displayText = isLong && !expanded ? `${trimmed.slice(0, maxLength)}...` : trimmed;

  return (
    <div
      style={{
        fontSize: 11.5,
        color: "#475569",
        marginTop: 2,
        lineHeight: 1.35,
        wordBreak: "break-word",
      }}
      data-testid={testId}
      title={trimmed}
    >
      <span>{displayText}</span>
      {isLong && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          style={{
            background: "none",
            border: "none",
            color: "#2563eb",
            cursor: "pointer",
            fontSize: 10.5,
            fontWeight: 600,
            padding: "0 0 0 4px",
            textDecoration: "underline",
          }}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {expanded ? "View less" : "View more"}
        </button>
      )}
    </div>
  );
}

function Metric({ label, value, caption, tone = "blue", active = false, onClick, testId }) {
  return (
    <div
      className={`metric ${tone} ${active ? "active" : ""}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        borderColor: active ? "var(--blue)" : undefined,
        background: active ? "#f8faff" : undefined,
      }}
      data-testid={testId || `metric-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
    >
      <div className="metric-top">
        <span>{label}</span>
      </div>
      <strong>{value}</strong>
      {caption && <small>{caption}</small>}
    </div>
  );
}

export default function Delivery({ user, onOpen, onOpenDeliveryPerson, onChange, routeParams }) {
  const isDelivery = isDeliveryUser(user);

  // If user is a Delivery Person, bypass the driver directory and show only their own queue directly
  if (isDelivery) {
    return (
      <DeliveryPersonDetail
        personId={user?.name || user?.user_id}
        user={user}
        onBack={null}
        onOpenOrder={onOpen}
        onOpenDelivery={onOpen}
        onChange={onChange}
      />
    );
  }

  return (
    <DeliveryDirectory
      user={user}
      onOpen={onOpen}
      onOpenDeliveryPerson={onOpenDeliveryPerson}
      onChange={onChange}
      routeParams={routeParams}
    />
  );
}

function DeliveryDirectory({ user, onOpen, onOpenDeliveryPerson, onChange, routeParams }) {
  // Filters
  const [dateFilter, setDateFilter] = useSavedState("delivery_main", "dateFilter", routeParams?.dateFilter || "all");
  const [statusFilter, setStatusFilter] = useSavedState("delivery_main", "statusFilter", routeParams?.statusFilter || "");
  const [companyFilter, setCompanyFilter] = useSavedState(
    "delivery_main",
    "company",
    routeParams?.company && routeParams.company !== "All Companies" ? routeParams.company : ""
  );
  const [search, setSearch] = useSavedState("delivery_main", "search", "");
  const [viewMode, setViewMode] = useState(routeParams?.statusFilter ? "deliveries" : "drivers");
  const [deliveriesPage, setDeliveriesPage] = useState(1);
  const [deliveriesPageSize, setDeliveriesPageSize] = useState(10);

  useEffect(() => {
    if (routeParams?.statusFilter !== undefined) {
      setStatusFilter(routeParams.statusFilter || "");
      if (routeParams.statusFilter) {
        setViewMode("deliveries");
      }
    }
    if (routeParams?.dateFilter !== undefined) {
      setDateFilter(routeParams.dateFilter || "all");
    }
    if (routeParams?.company !== undefined) {
      setCompanyFilter(routeParams.company !== "All Companies" ? routeParams.company : "");
    }
  }, [routeParams?.statusFilter, routeParams?.dateFilter, routeParams?.company, setStatusFilter, setDateFilter, setCompanyFilter]);

  const [allOrders, setAllOrders] = useState([]);
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1. Fetch all delivery queue records from backend
  const loadQueue = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api("get", "/delivery/queue", {
        params: { timeframe: "all" },
      });
      setAllOrders(res.data?.orders || []);
    } catch (err) {
      console.error("Failed to load delivery queue:", err);
      toast.error("Could not load delivery queue");
    } finally {
      setLoading(false);
    }
  }, []);

  // 2. Fetch delivery agents
  const loadAgents = useCallback(async () => {
    try {
      const res = await api("get", "/users?department=Delivery&active_only=true");
      const list = Array.isArray(res.data) ? res.data : [];
      const validAgents = list.filter(
        (u) =>
          !u.is_deleted &&
          u.is_active !== false &&
          ((u.department || "").toLowerCase() === "delivery" || (u.role || "").toLowerCase() === "delivery")
      );
      setDeliveryAgents(validAgents);
    } catch (err) {
      console.error("Failed to load delivery agents:", err);
      setDeliveryAgents([]);
    }
  }, []);

  useEffect(() => {
    loadQueue();
    loadAgents();
  }, [loadQueue, loadAgents]);

  // Scoped orders by company
  const scopedOrders = useMemo(() => {
    if (!companyFilter) return allOrders;
    return (allOrders || []).filter((o) => {
      const oComp = (o.company || "").toLowerCase();
      return !oComp || oComp === companyFilter.toLowerCase();
    });
  }, [allOrders, companyFilter]);

  // 3. Central workflow summary metrics across scoped orders
  const metrics = useMemo(() => {
    const active = scopedOrders.filter((o) => o.delivery_status !== "Delivered");
    const delivered = scopedOrders.filter((o) => o.delivery_status === "Delivered");
    const overdue = scopedOrders.filter(
      (o) => o.timeframe === "overdue" && o.delivery_status !== "Delivered"
    );
    const today = scopedOrders.filter(
      (o) => (o.timeframe === "today" || o.timeframe === "overdue") && o.delivery_status !== "Delivered"
    );

    return {
      active: active.length,
      today: today.length,
      overdue: overdue.length,
      delivered: delivered.length,
      total: scopedOrders.length,
    };
  }, [scopedOrders]);

  // Flat list of matching delivery orders
  const matchingDeliveryOrders = useMemo(() => {
    return scopedOrders.filter((order) => {
      // Date Filter
      if (dateFilter === "today") {
        if (order.timeframe !== "today" && order.timeframe !== "overdue") return false;
      } else if (dateFilter === "tomorrow") {
        if (order.timeframe !== "tomorrow") return false;
      } else if (dateFilter === "upcoming") {
        if (order.timeframe !== "upcoming") return false;
      } else if (dateFilter === "overdue") {
        if (order.timeframe !== "overdue") return false;
      }

      // Status Filter
      if (statusFilter) {
        const curStatus = (order.delivery_status || "Pending").toLowerCase();
        if (curStatus !== statusFilter.toLowerCase()) return false;
      }

      // Search Query Filter
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const match =
          (order.order_id || "").toLowerCase().includes(q) ||
          (order.upload_batch_id || "").toLowerCase().includes(q) ||
          (order.batch_id || "").toLowerCase().includes(q) ||
          (order.client_name || "").toLowerCase().includes(q) ||
          (order.deliver_to?.company_name || "").toLowerCase().includes(q) ||
          (order.deliver_to?.contact_person || "").toLowerCase().includes(q) ||
          (order.product || "").toLowerCase().includes(q) ||
          (order.description || "").toLowerCase().includes(q) ||
          (order.assigned_delivery_person || "").toLowerCase().includes(q) ||
          (order.deliver_to?.address || "").toLowerCase().includes(q) ||
          (order.pickup_from?.name || "").toLowerCase().includes(q);
        if (!match) return false;
      }

      return true;
    });
  }, [scopedOrders, dateFilter, statusFilter, search]);

  const paginatedDeliveryOrders = useMemo(() => {
    const start = (deliveriesPage - 1) * deliveriesPageSize;
    return matchingDeliveryOrders.slice(start, start + deliveriesPageSize);
  }, [matchingDeliveryOrders, deliveriesPage, deliveriesPageSize]);

  // 4. Group all orders by Delivery Person
  const driverGroups = useMemo(() => {
    const map = new Map();

    // Initialize all known delivery staff in the directory
    deliveryAgents.forEach((agent) => {
      const key = agent.name.trim().toLowerCase();
      map.set(key, {
        personId: agent.user_id || agent.name,
        name: agent.name,
        phone: agent.phone || "",
        email: agent.email || "",
        isUnassigned: false,
        allOrders: [],
      });
    });

    // Populate orders into groups
    scopedOrders.forEach((o) => {
      const assigned = (o.assigned_delivery_person || "").trim();
      const key = assigned ? assigned.toLowerCase() : "__unassigned__";

      if (!map.has(key)) {
        if (key === "__unassigned__") {
          map.set(key, {
            personId: "unassigned",
            name: "Unassigned Deliveries",
            phone: "",
            email: "",
            isUnassigned: true,
            allOrders: [],
          });
        } else {
          map.set(key, {
            personId: o.delivery_person_id || assigned,
            name: assigned,
            phone: o.delivery_person_phone || "",
            email: "",
            isUnassigned: false,
            allOrders: [],
          });
        }
      }

      const group = map.get(key);
      group.allOrders.push(o);
      if (!group.phone && o.delivery_person_phone) {
        group.phone = o.delivery_person_phone;
      }
    });

    // If unassigned has no orders, remove it; otherwise keep it
    if (map.has("__unassigned__") && map.get("__unassigned__").allOrders.length === 0) {
      map.delete("__unassigned__");
    }

    const list = Array.from(map.values()).map((group) => {
      const totalOrders = group.allOrders.length;
      const completedOrders = group.allOrders.filter((o) => o.delivery_status === "Delivered").length;
      const activeOrders = totalOrders - completedOrders;
      const overdueOrders = group.allOrders.filter(
        (o) => o.timeframe === "overdue" && o.delivery_status !== "Delivered"
      ).length;
      const outForDeliveryOrders = group.allOrders.filter(
        (o) => o.delivery_status === "Out for Delivery"
      ).length;

      return {
        ...group,
        totalOrders,
        activeOrders,
        completedOrders,
        overdueOrders,
        outForDeliveryOrders,
      };
    });

    // Sort: Unassigned first, then drivers by active orders descending, then alphabetical
    list.sort((a, b) => {
      if (a.isUnassigned) return -1;
      if (b.isUnassigned) return 1;
      if (b.activeOrders !== a.activeOrders) return b.activeOrders - a.activeOrders;
      return a.name.localeCompare(b.name);
    });

    return list;
  }, [scopedOrders, deliveryAgents]);

  // 5. Apply filters & search across the grouped drivers
  const { filteredDrivers, unassignedDriver } = useMemo(() => {
    const q = search.trim().toLowerCase();

    let unassigned = null;
    const regularDrivers = [];

    driverGroups.forEach((driver) => {
      const matchingOrders = driver.allOrders.filter((order) => {
        // Date Filter
        if (dateFilter === "today") {
          if (order.timeframe !== "today" && order.timeframe !== "overdue") return false;
        } else if (dateFilter === "tomorrow") {
          if (order.timeframe !== "tomorrow") return false;
        } else if (dateFilter === "upcoming") {
          if (order.timeframe !== "upcoming") return false;
        } else if (dateFilter === "overdue") {
          if (order.timeframe !== "overdue") return false;
        }

        // Status Filter
        if (statusFilter) {
          const curStatus = (order.delivery_status || "Pending").toLowerCase();
          if (curStatus !== statusFilter.toLowerCase()) return false;
        }

        // Search Query Filter
        if (q) {
          const matchDriver =
            driver.name.toLowerCase().includes(q) ||
            (driver.phone || "").toLowerCase().includes(q);

          const matchOrder =
            (order.order_id || "").toLowerCase().includes(q) ||
            (order.upload_batch_id || "").toLowerCase().includes(q) ||
            (order.batch_id || "").toLowerCase().includes(q) ||
            (order.client_name || "").toLowerCase().includes(q) ||
            (order.deliver_to?.company_name || "").toLowerCase().includes(q) ||
            (order.deliver_to?.contact_person || "").toLowerCase().includes(q) ||
            (order.product || "").toLowerCase().includes(q) ||
            (order.deliver_to?.address || "").toLowerCase().includes(q) ||
            (order.pickup_from?.name || "").toLowerCase().includes(q);

          if (!matchDriver && !matchOrder) return false;
        }

        return true;
      });

      const driverMatchesSearch =
        q && (driver.name.toLowerCase().includes(q) || (driver.phone || "").includes(q));

      if (
        !driverMatchesSearch &&
        (dateFilter !== "all" || statusFilter !== "" || q) &&
        matchingOrders.length === 0
      ) {
        return;
      }

      const matchingActive = matchingOrders.filter((o) => o.delivery_status !== "Delivered").length;
      const matchingCompleted = matchingOrders.filter((o) => o.delivery_status === "Delivered").length;
      const matchingOverdue = matchingOrders.filter(
        (o) => o.timeframe === "overdue" && o.delivery_status !== "Delivered"
      ).length;

      const enriched = {
        ...driver,
        matchingOrdersCount: matchingOrders.length,
        activeOrdersCount:
          dateFilter !== "all" || statusFilter !== "" || q ? matchingActive : driver.activeOrders,
        completedOrdersCount:
          dateFilter !== "all" || statusFilter !== "" || q
            ? matchingCompleted
            : driver.completedOrders,
        overdueOrdersCount:
          dateFilter !== "all" || statusFilter !== "" || q ? matchingOverdue : driver.overdueOrders,
      };

      if (driver.isUnassigned) {
        unassigned = enriched;
      } else {
        regularDrivers.push(enriched);
      }
    });

    return { filteredDrivers: regularDrivers, unassignedDriver: unassigned };
  }, [driverGroups, dateFilter, statusFilter, search]);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    paginatedItems: paginatedDrivers,
    totalItems: totalFilteredDrivers,
  } = usePagination(filteredDrivers, 12, { sectionKey: "delivery_main" });

  const hasActiveFilters = dateFilter !== "all" || statusFilter !== "" || companyFilter !== "" || search !== "";

  const handleResetFilters = () => {
    setDateFilter("all");
    setStatusFilter("");
    setCompanyFilter("");
    setSearch("");
    setCurrentPage(1);
    setDeliveriesPage(1);
  };

  const handleSelectDriver = (driver) => {
    if (onOpenDeliveryPerson) {
      onOpenDeliveryPerson(driver.personId || driver.name);
    }
  };

  return (
    <div className="content" data-testid="delivery-page">
      {/* Page Heading */}
      <div className="page-heading">
        <div>
          <div className="small-label">LOGISTICS &amp; FULFILLMENT</div>
          <h1>Delivery</h1>
          <p className="muted">
            Delivery Person-wise queue directory, driver assignments, and fulfillment history.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            type="button"
            className="icon-btn"
            onClick={loadQueue}
            title="Refresh Deliveries"
            data-testid="delivery-refresh-button"
            style={{ width: 36, height: 36 }}
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* 1. Summary Cards (CRM Standard Style) */}
      <div className="metrics-grid">
        <Metric
          label="Active Deliveries"
          value={metrics.active}
          caption={metrics.active === 1 ? "1 active shipment" : `${metrics.active} active shipments`}
          tone="blue"
          active={statusFilter === "Pending" || statusFilter === "Out for Delivery"}
          onClick={() => {
            setStatusFilter(statusFilter === "Pending" ? "" : "Pending");
            setCurrentPage(1);
          }}
          testId="delivery-metric-active"
        />
        <Metric
          label="Today's Deliveries"
          value={metrics.today}
          caption={metrics.today === 1 ? "1 drop today" : `${metrics.today} drops today`}
          tone="purple"
          active={dateFilter === "today"}
          onClick={() => {
            setDateFilter(dateFilter === "today" ? "all" : "today");
            setCurrentPage(1);
          }}
          testId="delivery-metric-today"
        />
        <Metric
          label="Overdue Deliveries"
          value={metrics.overdue}
          caption={metrics.overdue === 0 ? "All on track" : `${metrics.overdue} require attention`}
          tone="red"
          active={dateFilter === "overdue"}
          onClick={() => {
            setDateFilter(dateFilter === "overdue" ? "all" : "overdue");
            setCurrentPage(1);
          }}
          testId="delivery-metric-overdue"
        />
        <Metric
          label="Completed Deliveries"
          value={metrics.delivered}
          caption={`${metrics.delivered} fulfilled total`}
          tone="green"
          active={statusFilter === "Delivered"}
          onClick={() => {
            setStatusFilter(statusFilter === "Delivered" ? "" : "Delivered");
            setCurrentPage(1);
          }}
          testId="delivery-metric-completed"
        />
      </div>

      {/* Active Filter Banner */}
      {(statusFilter || (dateFilter && dateFilter !== "all") || companyFilter || search) && (
        <div className="active-filter-banner" style={{ marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>Active Filters:</span>
            {statusFilter && (
              <span className="badge blue" style={{ fontWeight: 700 }} data-testid="active-filter-delivery-status">
                Status: {statusFilter}
              </span>
            )}
            {dateFilter && dateFilter !== "all" && (
              <span className="badge amber" style={{ fontWeight: 700 }} data-testid="active-filter-delivery-date">
                Timeframe: {dateFilter}
              </span>
            )}
            {companyFilter && (
              <span className="badge purple" style={{ fontWeight: 700 }} data-testid="active-filter-delivery-company">
                Company: {companyFilter}
              </span>
            )}
            {search && (
              <span className="badge gray" style={{ fontWeight: 600 }} data-testid="active-filter-delivery-search">
                Search: "{search}"
              </span>
            )}
            <span className="badge gray" style={{ fontWeight: 600 }}>
              {matchingDeliveryOrders.length} matching {matchingDeliveryOrders.length === 1 ? "delivery" : "deliveries"}
            </span>
          </div>
          <button
            type="button"
            className="clear-btn"
            onClick={handleResetFilters}
            data-testid="clear-delivery-filters-btn"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* 2. Search & Filters Toolbar */}
      <div className="toolbar" data-testid="delivery-main-toolbar" style={{ marginTop: 14, marginBottom: 18 }}>
        <div className="search-box">
          <Search size={17} />
          <input
            data-testid="delivery-search-input"
            placeholder="Search delivery person, phone, order ID, client, product…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
              setDeliveriesPage(1);
            }}
          />
          {search && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
                setDeliveriesPage(1);
              }}
              data-testid="delivery-search-clear"
              style={{
                padding: 2,
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Date Filter Dropdown */}
        <select
          id="delivery-date-filter"
          data-testid="delivery-date-filter"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            setCurrentPage(1);
            setDeliveriesPage(1);
          }}
        >
          <option value="all">All Dates</option>
          <option value="today">Today &amp; Overdue</option>
          <option value="tomorrow">Tomorrow</option>
          <option value="upcoming">Upcoming</option>
          <option value="overdue">Overdue</option>
        </select>

        {/* Status Filter Dropdown */}
        <select
          id="delivery-status-filter"
          data-testid="delivery-status-filter"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
            setDeliveriesPage(1);
          }}
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending / Ready</option>
          <option value="Out for Delivery">Out for Delivery</option>
          <option value="Delivered">Delivered</option>
        </select>

        {/* Reset Filters */}
        {hasActiveFilters && (
          <button
            type="button"
            className="outline-btn compact"
            onClick={handleResetFilters}
            title="Reset filters"
            data-testid="delivery-reset-filters"
          >
            <RotateCcw size={13} /> Reset
          </button>
        )}

        {/* View Mode Toggle */}
        <div style={{ display: "inline-flex", alignItems: "center", background: "#f1f5f9", padding: 3, borderRadius: 8, gap: 4, marginLeft: "auto" }}>
          <button
            type="button"
            onClick={() => setViewMode("deliveries")}
            style={{
              padding: "5px 11px",
              fontSize: 12,
              fontWeight: viewMode === "deliveries" ? 700 : 500,
              background: viewMode === "deliveries" ? "#ffffff" : "transparent",
              color: viewMode === "deliveries" ? "var(--navy)" : "#64748b",
              border: 0,
              borderRadius: 6,
              boxShadow: viewMode === "deliveries" ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            data-testid="delivery-viewmode-deliveries-btn"
          >
            <Truck size={13} />
            <span>Deliveries ({matchingDeliveryOrders.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode("drivers")}
            style={{
              padding: "5px 11px",
              fontSize: 12,
              fontWeight: viewMode === "drivers" ? 700 : 500,
              background: viewMode === "drivers" ? "#ffffff" : "transparent",
              color: viewMode === "drivers" ? "var(--navy)" : "#64748b",
              border: 0,
              borderRadius: 6,
              boxShadow: viewMode === "drivers" ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            data-testid="delivery-viewmode-drivers-btn"
          >
            <Users size={13} />
            <span>By Staff ({filteredDrivers.length})</span>
          </button>
        </div>
      </div>

      {viewMode === "deliveries" ? (
        matchingDeliveryOrders.length === 0 ? (
          <div className="delivery-panel-empty" data-testid="delivery-empty-state">
            <div className="delivery-empty-icon">
              <Truck size={28} />
            </div>
            <h3>No deliveries found</h3>
            <p className="muted">
              {hasActiveFilters
                ? "No delivery records match your current filter criteria."
                : "There are currently no deliveries in the system."}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                className="primary-btn compact"
                onClick={handleResetFilters}
                style={{ marginTop: 14 }}
              >
                <RotateCcw size={13} /> Reset All Filters
              </button>
            )}
          </div>
        ) : (
          <div>
            <div className="table-wrap" style={{ marginTop: 4, background: "#ffffff", borderRadius: 8, border: "1px solid var(--line)" }}>
              <table className="data-table" data-testid="deliveries-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Client &amp; Destination</th>
                    <th>Product &amp; Qty</th>
                    <th>Delivery Staff</th>
                    <th>Status</th>
                    <th>Schedule / Date</th>
                    <th style={{ textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDeliveryOrders.map((o) => {
                    const statusTone =
                      o.delivery_status === "Delivered"
                        ? "green"
                        : o.delivery_status === "Out for Delivery"
                        ? "blue"
                        : "amber";
                    return (
                      <tr
                        key={o.order_id}
                        onClick={() => onOpen && onOpen(o.order_id)}
                        style={{ cursor: "pointer" }}
                        data-testid={`delivery-row-${o.order_id}`}
                      >
                        <td>
                          <b className="mono" style={{ color: "var(--navy)" }}>
                            {o.order_id}
                          </b>
                          {o.upload_batch_id && (
                            <div style={{ marginTop: 2 }}>
                              <span className="badge blue" style={{ fontSize: 10, padding: "1px 5px" }}>
                                #{o.upload_batch_id}
                              </span>
                            </div>
                          )}
                          {o.company && (
                            <small style={{ display: "block", color: "#64748b" }}>
                              {o.company}
                            </small>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{o.client_name || "—"}</div>
                          <small style={{ color: "#64748b" }}>
                            {o.deliver_to?.address || o.delivery_address || "—"}
                          </small>
                        </td>
                        <td>
                          <div>{o.product || "—"}</div>
                          <small style={{ color: "#64748b" }}>
                            {(o.quantity || 1).toLocaleString()} units
                          </small>
                          <OrderDescriptionSnippet
                            description={o.description}
                            maxLength={70}
                            testId={`delivery-table-description-${o.order_id}`}
                          />
                        </td>
                        <td>
                          {o.assigned_delivery_person ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <User size={13} style={{ color: "#64748b" }} />
                              <span style={{ fontWeight: 600, fontSize: 13 }}>
                                {o.assigned_delivery_person}
                              </span>
                            </div>
                          ) : (
                            <span className="badge amber" style={{ fontSize: 11 }}>
                              Unassigned
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${statusTone}`} style={{ fontWeight: 700 }}>
                            {o.delivery_status || "Pending"}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontSize: 12.5 }}>
                            {o.delivery_date || o.required_delivery_date || "—"}
                          </div>
                          {o.timeframe === "overdue" && o.delivery_status !== "Delivered" && (
                            <span className="badge red" style={{ fontSize: 10, padding: "1px 5px", marginTop: 2 }}>
                              Overdue
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button
                            type="button"
                            className="outline-btn compact"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpen && onOpen(o.order_id);
                            }}
                            style={{ fontSize: 12 }}
                          >
                            View Order →
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {matchingDeliveryOrders.length > deliveriesPageSize && (
              <div style={{ marginTop: 14 }}>
                <Pagination
                  currentPage={deliveriesPage}
                  totalItems={matchingDeliveryOrders.length}
                  pageSize={deliveriesPageSize}
                  onPageChange={setDeliveriesPage}
                  onPageSizeChange={setDeliveriesPageSize}
                  itemLabel="deliveries"
                />
              </div>
            )}
          </div>
        )
      ) : (
        <div>
          {/* 3. Unassigned Deliveries Banner (Visible when unassigned orders exist) */}
          {unassignedDriver && (unassignedDriver.activeOrdersCount > 0 || unassignedDriver.totalOrders > 0) && (
            <div
              className="delivery-unassigned-banner"
              onClick={() => handleSelectDriver(unassignedDriver)}
              data-testid="unassigned-deliveries-card"
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: "#fef3c7",
                    color: "#d97706",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#92400e" }}>
                    Unassigned Deliveries
                  </div>
                  <div style={{ fontSize: 12.5, color: "#78350f", marginTop: 2, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <span>
                      <b>{unassignedDriver.activeOrdersCount}</b> Active
                    </span>
                    <span>·</span>
                    <span>
                      <b>{unassignedDriver.completedOrdersCount}</b> Delivered
                    </span>
                    {unassignedDriver.overdueOrdersCount > 0 && (
                      <>
                        <span>·</span>
                        <span style={{ color: "#b91c1c", fontWeight: 700 }}>
                          {unassignedDriver.overdueOrdersCount} Overdue
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="outline-btn compact"
                style={{
                  background: "#ffffff",
                  borderColor: "#f59e0b",
                  color: "#b45309",
                  fontWeight: 700,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectDriver(unassignedDriver);
                }}
              >
                Assign Orders →
              </button>
            </div>
          )}

          {/* 4. Delivery Persons Cards Grid */}
          {loading && allOrders.length === 0 ? (
            <div className="delivery-panel-empty">
              <div className="loading-screen" style={{ margin: "24px auto" }}>
                Loading delivery persons…
              </div>
            </div>
          ) : filteredDrivers.length === 0 ? (
            <div className="delivery-panel-empty" data-testid="delivery-empty-state">
              <div className="delivery-empty-icon">
                <Truck size={28} />
              </div>
              <h3>No delivery persons found</h3>
              <p className="muted">
                {hasActiveFilters
                  ? "No delivery persons match your current search or filter criteria."
                  : "No delivery staff or orders found in the system."}
              </p>
              {hasActiveFilters && (
                <button
                  type="button"
                  className="primary-btn compact"
                  onClick={handleResetFilters}
                  style={{ marginTop: 14 }}
                  data-testid="delivery-empty-reset-button"
                >
                  <RotateCcw size={13} /> Reset All Filters
                </button>
              )}
            </div>
          ) : (
            <div className="delivery-driver-grid" data-testid="delivery-driver-grid">
              {paginatedDrivers.map((driver) => {
                const hasOverdue = driver.overdueOrdersCount > 0;
                const hasActive = driver.activeOrdersCount > 0;
                const isCompleted = !hasActive && driver.completedOrdersCount > 0;

                return (
                  <div
                    key={driver.personId || driver.name}
                    className="delivery-driver-card"
                    onClick={() => handleSelectDriver(driver)}
                    data-testid={`delivery-driver-card-${driver.personId}`}
                  >
                    {/* Card Top: Driver Name, Icon, Phone */}
                    <div className="delivery-driver-card-head">
                      <div>
                        <div className="delivery-driver-card-name">
                          <span>{driver.name}</span>
                        </div>
                        {driver.phone ? (
                          <a
                            href={`tel:${driver.phone}`}
                            className="delivery-driver-card-phone"
                            onClick={(e) => e.stopPropagation()}
                            title="Call delivery person"
                          >
                            <Phone size={12} style={{ color: "var(--blue)" }} />
                            <span>{driver.phone}</span>
                          </a>
                        ) : (
                          <span style={{ fontSize: 12, color: "#94a3b8" }}>No phone on file</span>
                        )}
                      </div>

                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          background: "#eff6ff",
                          color: "#2563eb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Truck size={16} />
                      </div>
                    </div>

                    {/* Card Middle: 3 Small Stat Columns */}
                    <div className="delivery-driver-stats-row">
                      <div className="delivery-stat-item">
                        <div className="stat-label">Active</div>
                        <div className={`stat-val ${hasActive ? "active-val" : ""}`}>
                          {driver.activeOrdersCount}
                        </div>
                      </div>
                      <div className="delivery-stat-item">
                        <div className="stat-label">Delivered</div>
                        <div className={`stat-val ${driver.completedOrdersCount > 0 ? "delivered-val" : ""}`}>
                          {driver.completedOrdersCount}
                        </div>
                      </div>
                      <div className="delivery-stat-item">
                        <div className="stat-label">Total</div>
                        <div className="stat-val">{driver.totalOrders}</div>
                      </div>
                    </div>

                    {/* Card Footer: Status Highlight & Action */}
                    <div className="delivery-driver-card-footer">
                      <div>
                        {hasOverdue ? (
                          <span className="badge red" style={{ fontSize: 11, padding: "2px 7px" }}>
                            {driver.overdueOrdersCount} Overdue
                          </span>
                        ) : driver.outForDeliveryOrders > 0 ? (
                          <span className="badge blue" style={{ fontSize: 11, padding: "2px 7px" }}>
                            {driver.outForDeliveryOrders} Out for Delivery
                          </span>
                        ) : isCompleted ? (
                          <span className="badge green" style={{ fontSize: 11, padding: "2px 7px" }}>
                            Queue Completed
                          </span>
                        ) : hasActive ? (
                          <span className="badge slate" style={{ fontSize: 11, padding: "2px 7px" }}>
                            {driver.activeOrdersCount} Active
                          </span>
                        ) : (
                          <span className="badge gray" style={{ fontSize: 11, padding: "2px 7px" }}>
                            Idle
                          </span>
                        )}
                      </div>

                      <span
                        style={{
                          fontSize: 12,
                          color: "var(--blue)",
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        View Deliveries →
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {filteredDrivers.length > pageSize && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalFilteredDrivers}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              itemLabel="delivery persons"
            />
          )}
        </div>
      )}
    </div>
  );
}
